import { useEffect, useState } from 'react'
import Head from 'next/head'
import { useQuery } from '@tanstack/react-query'
import {
  Trophy, Target, Zap, DollarSign, Info, ChevronLeft, ChevronRight,
  Bot, Clock, RefreshCw, AlertTriangle, Award
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'

const calculateSkillScore = (hitRate, wins, profitZar) =>
  Math.round((((wins || 0) * 3) + ((profitZar || 0) / 50) + ((hitRate || 0) / 10)) * 10) / 10

const getTier = (score, wins) => score >= 100 && wins >= 5 ? 'Elite' : score >= 50 || wins >= 3 ? 'Pro' : 'Rookie'

async function fetchLeaderboard({ page, pageSize, userId }) {
  const url = new URL('/api/leaderboard/all-time', window.location.origin)
  url.searchParams.set('limit', String(pageSize))
  url.searchParams.set('offset', String(page * pageSize))
  if (userId) url.searchParams.set('user_id', userId)
  const response = await fetch(url.toString(), { credentials: 'include' })
  if (!response.ok) throw new Error('Failed to load leaderboard')
  return response.json()
}

function useCountdown(targetIso) {
  const [left, setLeft] = useState({ days: 0, hours: 0 })
  useEffect(() => {
    const now = new Date()
    const target = targetIso || new Date(Date.UTC(
      now.getUTCMonth() === 11 ? now.getUTCFullYear() + 1 : now.getUTCFullYear(),
      now.getUTCMonth() === 11 ? 0 : now.getUTCMonth() + 1,
      1
    )).toISOString()
    const tick = () => {
      const difference = new Date(target).getTime() - Date.now()
      setLeft(difference > 0 ? {
        days: Math.floor(difference / 86400000),
        hours: Math.floor((difference % 86400000) / 3600000)
      } : { days: 0, hours: 0 })
    }
    tick()
    const timer = setInterval(tick, 60000)
    return () => clearInterval(timer)
  }, [targetIso])
  return left
}

function Avatar({ name, url, bot = false }) {
  const initials = name?.split(' ').map(word => word[0]).slice(0, 2).join('').toUpperCase() || '?'
  return url && !bot
    ? <img className="lb-avatar" src={url} alt="" />
    : <div className={`lb-avatar lb-avatar-initials ${bot ? 'lb-avatar-bot' : ''}`}>{bot ? <Bot size={16} /> : initials}</div>
}

function Tier({ tier, bot }) {
  return <span className={`lb-tier ${bot ? 'benchmark' : tier?.toLowerCase()}`}>{bot ? 'BENCHMARK' : tier}</span>
}

function ScoreBar({ score, max }) {
  return <div className="lb-score-bar" aria-hidden="true"><span style={{ width: `${Math.min(100, Math.max(5, (score / max) * 100))}%` }} /></div>
}

function Stat({ label, value, tone = '' }) {
  return <div className="lb-stat"><span>{label}</span><strong className={tone}>{value}</strong></div>
}

function SkillScoreInfo() {
  return (
    <span className="lb-info-wrap">
      <button className="lb-info-button" aria-label="How SkillScore works"><Info size={15} /></button>
      <span className="lb-tooltip">
        <b>How SkillScore works</b>
        <span>Decision quality is measured from simulated outcomes.</span>
        <span className="lb-formula">Wins × 3 + Profit ÷ 50 + Hit rate ÷ 10</span>
        <span>Consistency matters more than a single result.</span>
      </span>
    </span>
  )
}

function ChampionshipBanner({ prizeData, isLoading }) {
  const countdown = useCountdown(prizeData?.cycle_ends_at)
  const prizes = prizeData?.prizes || []
  const winners = prizeData?.last_winners || []
  const accents = ['gold', 'blue', 'bronze']
  return (
    <section className="lb-championship">
      <div className="lb-championship-heading">
        <div>
          <span className="lb-kicker">MONTHLY COMPETITION</span>
          <h1 data-testid="text-championship-title">Monthly Skill Championship</h1>
          <p>Top human forecasters earn virtual prizes on the first of every month.</p>
        </div>
        <div className="lb-cycle"><Clock size={15} /><span>Cycle ends in</span><b>{countdown.days}d {countdown.hours}h</b></div>
      </div>
      <div className="lb-prizes">
        {prizes.length ? prizes.map((prize, index) => (
          <div className={`lb-prize ${accents[index] || 'blue'}`} key={`${prize.rank}-${prize.prize_type}`}>
            <Award size={19} />
            <div><small>{prize.rank === 1 ? '1ST PLACE' : prize.rank === 2 ? '2ND PLACE' : '3RD PLACE'}</small><strong>{prize.label}</strong></div>
          </div>
        )) : <div className="lb-prizes-loading">{isLoading ? 'Loading championship prizes…' : 'Prize details will appear when the next championship is published.'}</div>}
      </div>
      <div className="lb-championship-foot">
        <span><b>SkillBot is a benchmark.</b> It is shown for comparison and never takes a human prize rank.</span>
        {winners.length > 0 && <div className="lb-winners"><span>Last champions</span>{winners.map(w => <b key={`${w.rank}-${w.username}`}>{w.username}</b>)}</div>}
      </div>
    </section>
  )
}

function LoadingRows() {
  return <div className="lb-loading" aria-label="Loading rankings">{[1, 2, 3, 4].map(item => <div className="lb-loading-row" key={item}><span /><span /><span /><span /></div>)}</div>
}

export default function LeaderboardPage() {
  const { user: authUser } = useAuth()
  const currentUserId = authUser?.user_id
  const [page, setPage] = useState(0)
  const pageSize = 20
  const leaderboard = useQuery({
    queryKey: ['leaderboard', page, currentUserId],
    queryFn: () => fetchLeaderboard({ page, pageSize, userId: currentUserId }),
    staleTime: 120000,
    placeholderData: previous => previous
  })
  const prize = useQuery({
    queryKey: ['monthly-prize'],
    queryFn: async () => {
      const response = await fetch('/api/leaderboard/monthly-prize', { credentials: 'include' })
      if (!response.ok) throw new Error('Failed to load prize info')
      return response.json()
    },
    staleTime: 600000
  })
  const rows = Array.isArray(leaderboard.data?.rows) ? leaderboard.data.rows : []
  const currentUserRank = leaderboard.data?.currentUserRank
  const currentHumanRank = currentUserRank?.humanRank
  const processedRows = rows.map((row, index) => {
    const bot = row.isBot === true
    const score = calculateSkillScore(row.hitRate, row.correct, row.totalProfitZar)
    return { ...row, isBot: bot, skillScore: score, tier: getTier(score, row.correct), displayRank: bot ? '—' : row.humanRank }
  })
  const maxScore = Math.max(...processedRows.map(row => row.skillScore), 100)
  const retry = () => leaderboard.refetch()

  return (
    <>
      <Head><title>Global Skill Rankings | SkillBets</title><meta name="description" content="Transparent football forecasting rankings from SkillBets." /></Head>
      <main className="lb-page">
        <div className="lb-disclaimer"><AlertTriangle size={14} /><span><b>Simulations and analytics only.</b> Prizes are virtual currency and Pass time — no cash wagering or payouts.</span></div>
        <div className="lb-shell">
          <ChampionshipBanner prizeData={prize.data} isLoading={prize.isLoading} />
          <section className="lb-rankings">
            <header className="lb-ranking-head">
              <div><span className="lb-kicker">ALL-TIME LIVE BOARD</span><div className="lb-title-line"><h2 data-testid="text-leaderboard-title">Global Skill Rankings</h2><SkillScoreInfo /></div><p>One transparent score blending accuracy, volume, and simulated returns.</p></div>
              <div className="lb-factors" aria-label="Ranking factors"><span><Target size={13} />Accuracy</span><span><Zap size={13} />Volume</span><span><DollarSign size={13} />Returns</span></div>
            </header>
            {currentUserRank && <div className="lb-your-rank" data-testid="current-user-rank"><div className="lb-your-rank-title"><div className="lb-your-icon"><Trophy size={18} /></div><div><span>Your human rank</span><strong>#{currentHumanRank}</strong></div></div><div className="lb-your-stats"><Stat label="Accuracy" value={`${Number(currentUserRank.hitRate || 0).toFixed(1)}%`} tone={currentUserRank.hitRate >= 60 ? 'positive' : ''} /><Stat label="Correct" value={currentUserRank.correct || 0} /><Stat label="Sim. returns" value={`R${Number(currentUserRank.totalProfitZar || 0).toFixed(0)}`} tone={currentUserRank.totalProfitZar >= 0 ? 'positive' : 'negative'} /></div></div>}
            <div className="lb-table-head"><span>Rank</span><span>User</span><span>SkillScore™</span><span>Performance</span></div>
            {leaderboard.isLoading && !leaderboard.data ? <LoadingRows /> : leaderboard.isError ? <div className="lb-state"><AlertTriangle size={20} /><b>Rankings are temporarily unavailable.</b><span>We couldn't refresh the live board.</span><button onClick={retry} data-testid="button-retry-leaderboard"><RefreshCw size={14} />Try again</button></div> : processedRows.length === 0 ? <div className="lb-state"><Trophy size={22} /><b>No rankings yet</b><span>Make a prediction to put your skill on the board.</span></div> : (
              <div className={`lb-rows ${leaderboard.isFetching ? 'is-refreshing' : ''}`}>
                {processedRows.map(user => {
                  const current = currentUserId && user.userId === currentUserId
                  const profit = Number(user.totalProfitZar || 0)
                  return <article className={`lb-row ${user.isBot ? 'is-bot' : ''} ${current ? 'is-current' : ''}`} key={user.userId} data-testid={`row-user-${user.userId}`}>
                    <div className="lb-rank">{user.isBot ? '—' : user.displayRank}</div>
                    <div className="lb-user"><Avatar name={user.name} url={user.avatarUrl} bot={user.isBot} /><div><div className="lb-name">{user.name}{current && <span className="lb-you">YOU</span>}</div><Tier tier={user.tier} bot={user.isBot} /></div></div>
                    <div className="lb-score"><strong className={user.isBot ? 'gold-text' : ''}>{user.skillScore.toFixed(1)}</strong><ScoreBar score={user.skillScore} max={maxScore} /></div>
                    <div className="lb-performance"><Stat label="Accuracy" value={`${Number(user.hitRate || 0).toFixed(1)}%`} tone={user.hitRate >= 60 ? 'positive' : ''} /><Stat label="Correct" value={user.correct || 0} /><Stat label="Sim. returns" value={`${profit >= 0 ? '+' : ''}R${profit.toFixed(0)}`} tone={profit >= 0 ? 'positive' : 'negative'} /></div>
                  </article>
                })}
              </div>
            )}
            <footer className="lb-pagination"><button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0 || leaderboard.isFetching} data-testid="button-prev-page"><ChevronLeft size={15} />Previous</button><span>Page <b>{page + 1}</b>{leaderboard.isFetching && <RefreshCw size={12} className="lb-spin" />}</span><button onClick={() => setPage(page + 1)} disabled={processedRows.length < pageSize || leaderboard.isFetching} data-testid="button-next-page">Next<ChevronRight size={15} /></button></footer>
          </section>
          {prize.isError && <div className="lb-prize-error">Championship details couldn't be loaded. The live rankings remain available.</div>}
          <p className="lb-bottom-note"><Info size={13} /> Every result is simulated. No real money is used, risked, or awarded.</p>
        </div>
      </main>
    </>
  )
}